import React, { useState, useEffect, useRef } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Label } from "reactstrap";
import "./CheckboxDropdown.css";

const CheckboxDropdown = ({ setValue }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const dropdownRef = useRef(null);

  // === API-compatible structure ===
  const structure = {
    "All Channel": ["direct", "franchise", "franchiseinfoline"], // leafs
    "all-franchise": ["franchise", "franchiseinfoline"], // grouping
  };

  // === All available options ===
  const allOptions = [
    "All Channel",
    "direct",
    "all-franchise",
    "franchise",
    "franchiseinfoline",
  ];

  // === UI-friendly labels ===
  const labels = {
    "All Channel": "All Channel",
    direct: "Direct",
    franchise: "Selco",
    franchiseinfoline: "Infoline",
    "all-franchise": "Franchise",
  };

  const toggleDropdown = () => setIsOpen((prev) => !prev);
  const isChecked = (key) => selected.includes(key);

  // === Handle selection logic ===
  const handleChange = (key) => {
    let updated = [...selected];

    // Add/remove children when selecting a parent
    const toggleChildren = (parent, add) => {
      (structure[parent] || []).forEach((child) => {
        updated = add
          ? [...new Set([...updated, child])]
          : updated.filter((i) => i !== child);
      });
    };

    if (isChecked(key)) {
      updated = updated.filter((item) => item !== key);
      if (structure[key]) toggleChildren(key, false);
    } else {
      updated.push(key);
      if (structure[key]) toggleChildren(key, true);
    }

    // Auto-sync "All Channel"
    const leafOptions = structure["All Channel"];
    const allSelected = leafOptions.every((i) => updated.includes(i));

    if (allSelected) {
      updated = [...new Set([...updated, "All Channel"])];
    } else {
      updated = updated.filter((i) => i !== "All Channel");
    }

    setSelected(updated);

    // === Set API value ===
    setTimeout(() => {
      const sel = updated.filter((item) => item !== "All Channel");
      const isFranchise = sel.includes("franchise");
      const isFranchiseInfo = sel.includes("franchiseinfoline");
      const isAllFranchise = sel.includes("all-franchise");
      const isDirect = sel.includes("direct");

      if (
        (isFranchise && isFranchiseInfo && sel.length === 2) ||
        isAllFranchise
      ) {
        setValue("all-franchise");
      } else if (sel.length === 1 && isFranchise) {
        setValue("selco");
      } else if (sel.length === 1 && isFranchiseInfo) {
        setValue("franchiseinfoline");
      } else if (sel.length === 1 && isDirect) {
        setValue("direct");
      } else if (
        sel.length === 3 &&
        isDirect &&
        isFranchise &&
        isFranchiseInfo
      ) {
        setValue("all");
      } else {
        setValue(sel[0] || "all");
      }
    }, 0);
  };

  // === Sync all-franchise checkbox with children ===
  useEffect(() => {
    const children = structure["all-franchise"];
    const allSelected = children.every((c) => selected.includes(c));
    if (allSelected && !selected.includes("all-franchise")) {
      setSelected((prev) => [...prev, "all-franchise"]);
    } else if (!allSelected && selected.includes("all-franchise")) {
      setSelected((prev) => prev.filter((i) => i !== "all-franchise"));
    }
  }, [selected]);

  // === Close dropdown when clicking outside ===
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // === Display label in header ===
  const getDisplayLabel = () => {
    const sel = selected.filter((item) => item !== "All Channel");
    if (selected.includes("All Channel")) return `All Channel (${sel.length})`;
    if (sel.length === 1) return `${labels[sel[0]]} (1)`;
    if (sel.length > 1) return `${labels[sel[0]]} (+${sel.length - 1})`;
    return "Select Channel";
  };

  return (
    <div className="dropdown-container" ref={dropdownRef}>
      <Label style={{ fontSize: 14, marginBottom: 8 }}>Channel</Label>
      <div className="dropdown-header" onClick={toggleDropdown}>
        <span>{getDisplayLabel()}</span>
        <span className="dropdown-arrow">
          <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
        </span>
      </div>

      {isOpen && (
        <div className="dropdown-list">
          {allOptions.map((channel) => (
            <label
              key={channel}
              className={`dropdown-item ${
                ["franchise", "franchiseinfoline"].includes(channel)
                  ? "dropdown-subitem"
                  : ""
              }`}
            >
              <input
                type="checkbox"
                checked={isChecked(channel)}
                onChange={() => handleChange(channel)}
              />
              {labels[channel]}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default CheckboxDropdown;
